/**
 * @fileoverview Flagged Transactions — transaction review table
 * @module jobs/phases/review/flaggedTransactions/TransactionTable
 *
 * Shared by the focused head view (single head) and the all-flags view
 * (extra Agent column). Filters: search / risk / decision. Row selection
 * with select-all-visible, bulk agree/disagree, add-manual-flag, and row
 * click → review modal.
 */

import { Fragment, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Flag,
  RotateCcw,
  GitPullRequestArrow,
  Loader2,
  Lock,
  Plus,
  Search,
  X,
} from "lucide-react";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Checkbox } from "@/ui/primitives/checkbox";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { cn } from "@/utils/misc";
import { BulkActionBar, BulkActionButton } from "@/ui/composed/bulk-action-bar";
import { TransactionReviewModal } from "./TransactionReviewModal";
import { FlagExportMenu } from "./FlagExportMenu";
import {
  agentText,
  fmtDate,
  fmtMoney,
  pursuedAgainst,
  titleCase,
  HEAD_RESPONDENT,
  RISK_BADGE,
  DECISION_BADGE,
  HEAD_BADGE,
  type FlagRowUI,
  type FlaggedHeadKey,
} from "./flaggedShared";
import { groupRows, type GroupByMode } from "./flaggedGrouping";
import { FacetedFilter } from "@/ui/composed/filters/faceted-filter";
import { usePersistedState } from "@/utils/use-persisted-state";

export function TransactionTable(props: {
  jobId: Id<"jobs">;
  /** Restrict to one head; omit for the all-flags view. */
  headKey?: FlaggedHeadKey;
  isViewer: boolean;
  /** Jump to a head view (parallel-head chip in the review modal). */
  onNavigateToHead?: (headKey: FlaggedHeadKey) => void;
}) {
  const { jobId, headKey, isViewer, onNavigateToHead } = props;
  // Filter prefs persist to localStorage, scoped per job + table context
  // (the all-flags view and each focused head keep their own filters).
  const pk = `flagged:filters:${jobId}:${headKey ?? "all"}`;
  const [search, setSearch] = usePersistedState<string>(`${pk}:search`, "");
  // Multi-select filters — empty array means "no filter" (show all).
  const [risk, setRisk] = usePersistedState<string[]>(`${pk}:risk`, []);
  const [decision, setDecision] = usePersistedState<string[]>(
    `${pk}:decision`,
    [],
  );
  const [agent, setAgent] = usePersistedState<string[]>(`${pk}:agent`, []);
  const [counterparty, setCounterparty] = usePersistedState<string[]>(
    `${pk}:counterparty`,
    [],
  );
  // "Pursued against" — the claim's respondent (director(s) for director-
  // liability heads, the counterparty otherwise), distinct from the
  // transaction counterparty. See pursuedAgainst in flaggedShared.
  const [respondent, setRespondent] = usePersistedState<string[]>(
    `${pk}:respondent`,
    [],
  );
  const [groupBy, setGroupBy] = usePersistedState<GroupByMode>(
    `${pk}:groupBy`,
    "none",
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openFlagId, setOpenFlagId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggleGroup = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const { data } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.flaggedTransactions.publicQueries.listFlags,
      {
        jobId,
        headKey,
        // risk/decision/agent are multi-select → filtered client-side below.
        search: search || undefined,
      },
    ),
  );
  const allRows = useMemo(() => (data ?? []) as FlagRowUI[], [data]);
  // Search placeholder names the focused head ("Search insolvent trading…")
  // when the table is scoped to one; the all-flags view searches by
  // counterparty across every head.
  const searchPlaceholder = useMemo(() => {
    if (!headKey) return "Search counterparty…";
    const label = allRows.find((r) => r.headKey === headKey)?.headLabel;
    return label ? `Search ${label.toLowerCase()}…` : "Search…";
  }, [headKey, allRows]);
  // Distinct heads present in the data — the Agent filter options.
  const agentOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of allRows) m.set(r.headKey, r.headLabel);
    return [...m.entries()].map(([key, label]) => ({ key, label }));
  }, [allRows]);
  // Distinct counterparties present in the data — the Counterparty filter
  // options (sorted alphabetically).
  const counterpartyOptions = useMemo(() => {
    const names = new Set<string>();
    for (const r of allRows)
      if (r.counterpartyName) names.add(r.counterpartyName);
    return [...names]
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ value: name, label: name }));
  }, [allRows]);
  // Distinct respondents ("pursued against") present in the data.
  const respondentOptions = useMemo(() => {
    const names = new Set<string>();
    for (const r of allRows) names.add(pursuedAgainst(r));
    return [...names]
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ value: name, label: name }));
  }, [allRows]);
  const rows = useMemo(
    () =>
      allRows.filter(
        (r) =>
          (agent.length === 0 || agent.includes(r.headKey)) &&
          (counterparty.length === 0 ||
            counterparty.includes(r.counterpartyName)) &&
          (respondent.length === 0 || respondent.includes(pursuedAgainst(r))) &&
          (risk.length === 0 || (r.risk !== null && risk.includes(r.risk))) &&
          (decision.length === 0 || decision.includes(r.decision)),
      ),
    [allRows, agent, counterparty, respondent, risk, decision],
  );
  const groups = useMemo(() => groupRows(rows, groupBy), [rows, groupBy]);
  const allCollapsed =
    groups.length > 0 && groups.every((g) => collapsed.has(g.key));
  const toggleAllGroups = () =>
    setCollapsed(allCollapsed ? new Set() : new Set(groups.map((g) => g.key)));

  const renderRow = (row: FlagRowUI) => (
    <TableRow
      key={row.flagId}
      className={cn(
        "cursor-pointer",
        row.needsReview && "bg-amber-50/40 dark:bg-amber-950/10",
      )}
      onClick={() => setOpenFlagId(row.flagId)}
    >
      <TableCell onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={selected.has(row.flagId)}
          onCheckedChange={() => toggleOne(row.flagId)}
          aria-label="Select row"
        />
      </TableCell>
      {!headKey && (
        <TableCell>
          <Badge
            color={HEAD_BADGE[row.headKey]}
            variant="soft"
            size="sm"
            className="whitespace-nowrap"
          >
            {titleCase(row.headLabel)}
          </Badge>
        </TableCell>
      )}
      <TableCell className="text-xs tabular-nums">
        {row.transactionDate
          ? fmtDate(row.transactionDate)
          : row.isGrouped
            ? "Various"
            : "—"}
      </TableCell>
      <TableCell>
        <div className="text-sm font-medium">{row.counterpartyName}</div>
        {row.counterpartyRelationship && (
          <div className="text-[11px] text-amber-700 dark:text-amber-400">
            {titleCase(row.counterpartyRelationship)}
          </div>
        )}
        {/* Respondent differs from the counterparty on director-liability
            heads — say who the claim is actually against so the counterparty
            never reads as the target of the claim. */}
        {pursuedAgainst(row) !== row.counterpartyName && (
          <div
            className="text-[11px] text-muted-foreground"
            title={HEAD_RESPONDENT[row.headKey].note}
          >
            Pursued against: {pursuedAgainst(row)}
          </div>
        )}
      </TableCell>
      <TableCell>
        <p className="line-clamp-2 text-xs text-muted-foreground">
          {agentText(row.description)}
        </p>
      </TableCell>
      <TableCell className="text-right">
        <span className="font-mono text-sm tabular-nums text-muted-foreground">
          {fmtMoney(row.amount)}
        </span>
      </TableCell>
      <TableCell className="text-right">
        {/* An unquantified recoverable is not a nil one, and only the detail
            panel carries the badge — so the cell withholds a figure rather than
            asserting $0. The head panel explains why. */}
        {row.valuationRequired && (row.quantum ?? 0) === 0 ? (
          <span
            className="font-mono text-sm tabular-nums text-muted-foreground"
            title="Not quantified — the recoverable turns on a figure the current material can't supply."
          >
            —
          </span>
        ) : (
          <span
            className="font-mono text-sm font-semibold tabular-nums"
            title={
              row.quantum !== null
                ? "Recoverable estimate"
                : "No estimate set — defaults to the gross amount"
            }
          >
            {fmtMoney(row.quantum ?? row.amount)}
          </span>
        )}
      </TableCell>
      <TableCell>
        {row.risk ? (
          <Badge color={RISK_BADGE[row.risk].color} variant="soft" size="sm">
            {RISK_BADGE[row.risk].label}
          </Badge>
        ) : (
          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <Loader2 className="h-3 w-3" /> unreviewed
          </span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Badge
            color={DECISION_BADGE[row.decision].color}
            variant="soft"
            size="sm"
          >
            {DECISION_BADGE[row.decision].label}
          </Badge>
          {(row.rowLocked || row.decisionLocked) && (
            <Lock className="h-3 w-3 text-violet-500" />
          )}
          {row.hasProposed && (
            <GitPullRequestArrow className="h-3 w-3 text-violet-500" />
          )}
          {/* s588FE(5) reaches only a s588FA / s588FB transaction. Rows with
              no recorded character need review, regardless of parallel heads
              (MOR-1264). */}
          {row.characterMissing && (
            <span title="Raised under s588FE(5) with no recorded s588FA / s588FB character. Record the applicable test and the basis explaining how it applies. Re-run the head or review the transaction.">
              <Badge
                color="amber"
                variant="soft"
                size="sm"
                className="whitespace-nowrap"
              >
                No s588FA/FB character
              </Badge>
            </span>
          )}
        </div>
      </TableCell>
      {/* Per-transaction export (MOR-808) - "just print this one preference". */}
      <TableCell className="w-8 py-0 pr-2" onClick={(e) => e.stopPropagation()}>
        <FlagExportMenu jobId={jobId} flagIds={[row.flagId]} variant="icon" />
      </TableCell>
    </TableRow>
  );

  const { mutateAsync: setDecisionMutation, isPending: decisionPending } =
    useMutation({
      mutationFn: useConvexMutation(
        api.jobs.summarization.agents.flaggedTransactions.publicMutations
          .setDecision,
      ),
    });

  const allVisibleSelected =
    rows.length > 0 && rows.every((r) => selected.has(r.flagId));
  const someVisibleSelected = rows.some((r) => selected.has(r.flagId));

  const toggleAll = () => {
    if (allVisibleSelected) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.flagId)));
  };

  const toggleOne = (flagId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(flagId)) next.delete(flagId);
      else next.add(flagId);
      return next;
    });
  };

  const bulkDecide = async (target: "accepted" | "rejected" | "pending") => {
    const ids = [...selected] as Id<"flaggedTransactions">[];
    if (ids.length === 0) return;
    try {
      // The backend caps a single call at 100 flags, so a large selection
      // (a job can carry 200+) is sent in batches and the results summed.
      const BATCH = 100;
      let updated = 0;
      let skippedLocked = 0;
      for (let i = 0; i < ids.length; i += BATCH) {
        const result = await setDecisionMutation({
          jobId,
          flagIds: ids.slice(i, i + BATCH),
          decision: target,
        });
        const { updated: u, skippedLocked: s } = result as {
          updated: number;
          skippedLocked: number;
        };
        updated += u;
        skippedLocked += s;
      }
      toast.success(
        `${updated} transaction${updated === 1 ? "" : "s"} ${target === "pending" ? "returned to review" : target}${skippedLocked > 0 ? ` (${skippedLocked} locked — skipped)` : ""}`,
      );
      setSelected(new Set());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Bulk update failed");
    }
  };

  const totals = useMemo(
    () => ({
      count: rows.length,
      value: rows.reduce((s, r) => s + (r.quantum ?? r.amount), 0),
    }),
    [rows],
  );

  // Selection survives filter changes (the bulk decisions act on every ticked
  // flag, visible or not) - the export matches that, ordered by the table's
  // underlying sort so the document reads top-down.
  const selectedFlagIds = useMemo(
    () => allRows.filter((r) => selected.has(r.flagId)).map((r) => r.flagId),
    [allRows, selected],
  );

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      {/* Filters + bulk actions */}
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
        <div className="relative min-w-[12rem] flex-1">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-8 pl-9 text-sm"
          />
        </div>
        <FacetedFilter
          title="Pursued Against"
          options={respondentOptions}
          selectedValues={respondent}
          onSelectionChange={setRespondent}
        />
        <FacetedFilter
          title="Counterparty"
          options={counterpartyOptions}
          selectedValues={counterparty}
          onSelectionChange={setCounterparty}
        />
        {!headKey && (
          <FacetedFilter
            title="Agent"
            options={agentOptions.map((a) => ({
              value: a.key,
              label: titleCase(a.label),
            }))}
            selectedValues={agent}
            onSelectionChange={setAgent}
          />
        )}
        <FacetedFilter
          title="Risk"
          options={[
            { value: "high", label: "High" },
            { value: "medium", label: "Medium" },
            { value: "low", label: "Low" },
          ]}
          selectedValues={risk}
          onSelectionChange={setRisk}
        />
        <FacetedFilter
          title="Decision"
          options={[
            { value: "pending", label: "Pending" },
            { value: "accepted", label: "Agreed" },
            { value: "partially_accepted", label: "Partially Agreed" },
            { value: "rejected", label: "Disagreed" },
          ]}
          selectedValues={decision}
          onSelectionChange={setDecision}
        />
        <Select
          value={groupBy}
          onValueChange={(v) => setGroupBy(v as GroupByMode)}
        >
          <SelectTrigger className="h-8 w-40 text-xs">
            <SelectValue placeholder="Group by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No Grouping</SelectItem>
            <SelectItem value="respondent">Group: Pursued Against</SelectItem>
            <SelectItem value="counterparty">Group: Counterparty</SelectItem>
            {!headKey && <SelectItem value="agent">Group: Agent</SelectItem>}
            <SelectItem value="risk">Group: Risk</SelectItem>
            <SelectItem value="decision">Group: Decision</SelectItem>
            <SelectItem value="event">Group: Linked Event</SelectItem>
          </SelectContent>
        </Select>
        {groupBy !== "none" && groups.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-xs"
            onClick={toggleAllGroups}
          >
            {allCollapsed ? (
              <>
                <ChevronDown className="h-3.5 w-3.5" /> Expand All
              </>
            ) : (
              <>
                <ChevronRight className="h-3.5 w-3.5" /> Collapse All
              </>
            )}
          </Button>
        )}
        <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Badge
            color="red"
            variant="soft"
            size="sm"
            className="gap-1 whitespace-nowrap"
          >
            <Flag className="h-3 w-3" />
            {totals.count}
          </Badge>
          {fmtMoney(totals.value)}
        </span>
        {!isViewer && headKey && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1 text-xs"
            onClick={() => setAddOpen(true)}
          >
            <Plus className="h-3 w-3" /> Add transaction
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground">
          <Flag className="h-3.5 w-3.5" />
          No transactions match.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8">
                <Checkbox
                  checked={
                    allVisibleSelected
                      ? true
                      : someVisibleSelected
                        ? "indeterminate"
                        : false
                  }
                  onCheckedChange={toggleAll}
                  aria-label="Select all visible"
                />
              </TableHead>
              {!headKey && <TableHead>Agent</TableHead>}
              <TableHead>Date</TableHead>
              <TableHead>Counterparty</TableHead>
              <TableHead className="w-[28%]">Summary</TableHead>
              <TableHead className="whitespace-nowrap text-right">
                Amount
              </TableHead>
              <TableHead className="whitespace-nowrap text-right">
                Est Recoverable
              </TableHead>
              <TableHead>Risk</TableHead>
              <TableHead>Decision</TableHead>
              <TableHead className="w-8">
                <span className="sr-only">Export</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groupBy === "none"
              ? rows.map(renderRow)
              : groups.map((g) => {
                  const isCollapsed = collapsed.has(g.key);
                  return (
                    <Fragment key={g.key}>
                      <TableRow
                        className="cursor-pointer bg-muted/40 hover:bg-muted/60"
                        onClick={() => toggleGroup(g.key)}
                      >
                        {/* Label spans the left columns; Amount + Est
                            Recoverable sit under their own column headers. */}
                        <TableCell colSpan={headKey ? 4 : 5} className="py-2">
                          <div className="flex items-center gap-2">
                            {isCollapsed ? (
                              <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                            ) : (
                              <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                            )}
                            <span
                              className="w-[26rem] shrink-0 truncate text-sm font-semibold"
                              title={g.label}
                            >
                              {g.label}
                            </span>
                            <Badge
                              color="red"
                              variant="soft"
                              size="sm"
                              className="shrink-0 gap-1 whitespace-nowrap"
                            >
                              <Flag className="h-3 w-3" />
                              {g.rows.length}
                            </Badge>
                            {groupBy === "event" && g.rows.length > 1 && (
                              <Badge
                                color="green"
                                variant="soft"
                                size="sm"
                                className="shrink-0 whitespace-nowrap"
                              >
                                {g.headKeys.length} Claims
                              </Badge>
                            )}
                            {/* Per-head chips only when collapsed — when
                                expanded the Agent column already shows them. */}
                            {isCollapsed &&
                              groupBy === "event" &&
                              g.headKeys.length > 1 && (
                                <span className="flex min-w-0 flex-wrap gap-1">
                                  {g.headKeys.map((hk) => (
                                    <Badge
                                      key={hk}
                                      color={HEAD_BADGE[hk]}
                                      variant="soft"
                                      size="sm"
                                      className="whitespace-nowrap"
                                    >
                                      {titleCase(
                                        g.rows.find((r) => r.headKey === hk)
                                          ?.headLabel ?? hk,
                                      )}
                                    </Badge>
                                  ))}
                                </span>
                              )}
                          </div>
                        </TableCell>
                        <TableCell className="py-2 text-right font-mono text-xs tabular-nums text-muted-foreground">
                          {fmtMoney(g.amount)}
                        </TableCell>
                        <TableCell className="py-2 text-right font-mono text-xs font-semibold tabular-nums">
                          {fmtMoney(g.recoverable)}
                        </TableCell>
                        {/* Risk + Decision + the export action column. */}
                        <TableCell colSpan={3} className="py-2" />
                      </TableRow>
                      {!isCollapsed && g.rows.map(renderRow)}
                    </Fragment>
                  );
                })}
          </TableBody>
        </Table>
      )}

      {openFlagId && (
        <TransactionReviewModal
          jobId={jobId}
          flagId={openFlagId as Id<"flaggedTransactions">}
          isViewer={isViewer}
          onClose={() => setOpenFlagId(null)}
          position={(() => {
            const index = rows.findIndex((r) => r.flagId === openFlagId);
            return index >= 0 ? { index, total: rows.length } : undefined;
          })()}
          onNavigate={(direction) => {
            const index = rows.findIndex((r) => r.flagId === openFlagId);
            const next = rows[index + direction];
            if (next) setOpenFlagId(next.flagId);
          }}
          onOpenFlag={(flagId) => setOpenFlagId(flagId)}
          onNavigateToHead={onNavigateToHead}
        />
      )}
      {addOpen && headKey && (
        <AddManualFlagDialog
          jobId={jobId}
          headKey={headKey}
          onClose={() => setAddOpen(false)}
        />
      )}

      {/* Floating bulk-action bar (checkbox selection) — mirrors the pattern
          used across the summary agents. */}
      <BulkActionBar
        count={selected.size}
        onDone={() => setSelected(new Set())}
      >
        {!isViewer && (
          <>
            <BulkActionButton
              disabled={decisionPending}
              onClick={() => void bulkDecide("accepted")}
            >
              <Check className="h-3 w-3" /> Agree
            </BulkActionButton>
            <BulkActionButton
              tone="danger"
              disabled={decisionPending}
              onClick={() => void bulkDecide("rejected")}
            >
              <X className="h-3 w-3" /> Disagree
            </BulkActionButton>
            <BulkActionButton
              disabled={decisionPending}
              onClick={() => void bulkDecide("pending")}
            >
              <RotateCcw className="h-3 w-3" /> Return to Review
            </BulkActionButton>
          </>
        )}
        {/* Export the ticked rows (MOR-808) — viewers can export too. */}
        <FlagExportMenu
          jobId={jobId}
          flagIds={selectedFlagIds}
          label="Export selected"
        />
      </BulkActionBar>
    </div>
  );
}

function AddManualFlagDialog(props: {
  jobId: Id<"jobs">;
  headKey: FlaggedHeadKey;
  onClose: () => void;
}) {
  const { jobId, headKey, onClose } = props;
  const [counterparty, setCounterparty] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");

  const { mutateAsync: addFlag, isPending } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.flaggedTransactions.publicMutations
        .addManualFlag,
    ),
  });

  const submit = async () => {
    const parsedAmount = Number(amount);
    if (
      !counterparty.trim() ||
      !description.trim() ||
      !Number.isFinite(parsedAmount)
    ) {
      toast.error("Counterparty, summary, and a numeric amount are required");
      return;
    }
    try {
      await addFlag({
        jobId,
        headKey,
        counterpartyName: counterparty.trim(),
        description: description.trim(),
        amount: parsedAmount,
        transactionDate: date || undefined,
        notes: notes.trim() || undefined,
      });
      toast.success("Transaction added");
      onClose();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to add transaction",
      );
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add transaction</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">Counterparty</Label>
            <Input
              value={counterparty}
              onChange={(e) => setCounterparty(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">Summary</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs">Amount (AUD)</Label>
              <Input
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="decimal"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs">Date</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" disabled={isPending} onClick={() => void submit()}>
            {isPending && (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            )}
            Add transaction
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
